import { useQueryClient } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import {
  Button,
  Checkbox,
  ConnectionItem,
  FileUploadTrigger,
  Flex,
  List,
  ProgressBar,
  SidePanel,
  Tag,
  Text,
  useMediaQuery,
} from "@wayflyer/flyui";
import type { UseUploadManagerReturn } from "@wayflyer/flyui";
import { IconBank24Line, IconPage24Line } from "@wayflyer/flyui-icons/24/line";
import type {
  BankAccountGroupSchema,
  BankAccountGroupStatementMemberSchema,
  BankConnectSchema,
  LogoLoadingState,
} from "bankconnect";
import { ReconnectBankAccountModal } from "bankconnect";
import dayjs from "dayjs";
import { usePostHog } from "posthog-js/react";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { AuthCallbackPath, useErrorModal } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import styles from "./group-details-panel.module.css";

import { BankLogoDisplay } from "../../../../components";
import {
  BANK_STATEMENT_ACCEPTED_FILE_TYPES,
  BANK_STATEMENT_ACCEPTED_FILE_TYPES_PDF_ONLY,
  DEFAULT_GROUP_NAME,
  UPLOAD_STATEMENTS_SIZE_LIMIT_MB,
} from "../../../../components/constants";
import { useBankStatementSubmitByUuidMutation } from "../../../../queries/bank-connect/bank-statement.mutations";
import {
  invalidateBankingSourceQueries,
  useSetCoverageAcknowledgement,
} from "../../../../queries/bank-connect/queries";
import { setReopenGroupIntent } from "../../hooks/reopen-group-panel-storage";
import type { BankStatementUpload } from "../../hooks/use-bank-accounts-upload-manager";
import { EXTRACTION_FAILED_METADATA } from "../../hooks/use-bank-statement-uploads";
import { useRemoveStatementAction } from "../../hooks/use-remove-statement-action";
import type { ReassignedConnectionMember } from "../../hooks/use-reassigned-connections-for-group";
import type { ReassignedStatementMember } from "../../hooks/use-reassigned-statements-for-group";
import { AssignStatementModal } from "../assign-statement-modal/assign-statement-modal";
import { StatementRow } from "../components/statement-row";
import { MESSAGES } from "../message-definitions";
import { MonthsCoverageBar } from "../months-coverage-bar/months-coverage-bar";
import { RemoveStatementModal } from "../remove-statement-modal/remove-statement-modal";
import {
  capitalize,
  capitalizeFirstLetter,
  getAccountLabel,
  getAccountReadinessBadge,
  getAccountReviewState,
  getCompleteCoverageBodyText,
  getConnectionMemberSubtitle,
  getCoverageGapPeriods,
  getGroupAccountLabel,
  getMonthCoverage,
  getStatementCoverageBodyText,
  hasDisconnectedConnectionMember,
  hasStatementCoverageGap,
  isCsvEligibleGap,
  markGapMonthsReviewed,
} from "../utils";

type GroupDetailsPanelProps = {
  isOpen: boolean;
  group: BankAccountGroupSchema | null;
  bankConnectProps: BankConnectSchema;
  accountGroups: BankAccountGroupSchema[];
  companyDomain: string;
  logoLoadingStates: LogoLoadingState;
  requiredStatementMonths: number;
  reassignedMembers: ReassignedConnectionMember[];
  reassignedStatementMembers: ReassignedStatementMember[];
  onClose: () => void;
  onCloseWithActiveUploads: () => void;
  onConnect: () => void;
  onFilesAdded: UseUploadManagerReturn<BankStatementUpload>["onFilesAdded"];
  onFileRetry: UseUploadManagerReturn<BankStatementUpload>["onFileRetry"];
  uploads: UseUploadManagerReturn<BankStatementUpload>["uploads"];
  uploadsState: UseUploadManagerReturn<BankStatementUpload>["uploadsState"];
  isRefetchingBankingSources: boolean;
  onStatementsChanged: () => void;
};

const ACTIVE_UPLOAD_STATUSES = new Set(["queued", "uploading", "processing"]);

const uploadProgress = (upload: { progress?: number; status: string }) =>
  upload.progress ?? (upload.status === "complete" || upload.status === "processing" ? 100 : 0);

const uploadedAtTime = ({ member }: { member: BankAccountGroupStatementMemberSchema }) =>
  Date.parse(member.uploadedAt) || 0;

const sortNewestFirst = <Row extends { member: BankAccountGroupStatementMemberSchema }>(rows: Row[]) =>
  [...rows].sort((a, b) => uploadedAtTime(b) - uploadedAtTime(a));

export const GroupDetailsPanel = ({
  isOpen,
  group,
  bankConnectProps,
  accountGroups,
  companyDomain,
  logoLoadingStates,
  requiredStatementMonths,
  reassignedMembers,
  reassignedStatementMembers,
  onClose,
  onCloseWithActiveUploads,
  onConnect,
  onFilesAdded,
  onFileRetry,
  uploads,
  uploadsState,
  isRefetchingBankingSources,
  onStatementsChanged,
}: GroupDetailsPanelProps) => {
  const intl = useIntl();
  const query = useMediaQuery();
  const isMobile = query("sm").windowLte;
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const queryClient = useQueryClient();
  const { openErrorModal } = useErrorModal();
  const submitByUuid = useBankStatementSubmitByUuidMutation(customerDomain);
  const setCoverageAcknowledgement = useSetCoverageAcknowledgement(customerDomain);
  const { removeStatement, isPending: isRemovePending } = useRemoveStatementAction(companyDomain);
  const posthog = usePostHog();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [pendingFiles, setPendingFiles] = useState<Set<File>>(new Set());
  const [hasSeenRefetchStart, setHasSeenRefetchStart] = useState(false);
  const [rejectedFileNames, setRejectedFileNames] = useState<string[]>([]);
  const [statementToReassign, setStatementToReassign] = useState<BankAccountGroupStatementMemberSchema | null>(null);
  const [statementToRemove, setStatementToRemove] = useState<BankAccountGroupStatementMemberSchema | null>(null);
  const [reconnectingConnectionId, setReconnectingConnectionId] = useState<string | null>(null);

  if (!group) {
    return null;
  }

  const handleFilesAdded: UseUploadManagerReturn<BankStatementUpload>["onFilesAdded"] = (files, rejectedFiles) => {
    setPendingFiles((current) => new Set([...current, ...files]));
    setRejectedFileNames((rejectedFiles ?? []).map((rejection) => rejection.file.name));
    onFilesAdded(files, rejectedFiles);
  };

  const panelUploads = uploadsState.uploads.filter((_, index) => {
    const file = uploads[index]?.file;
    return file != null && pendingFiles.has(file);
  });
  const activeUploads = panelUploads.filter((upload) => ACTIVE_UPLOAD_STATUSES.has(upload.status));
  const combinedProgress = panelUploads.length
    ? Math.round(panelUploads.reduce((sum, upload) => sum + uploadProgress(upload), 0) / panelUploads.length)
    : 0;
  const isProcessing = activeUploads.length > 0 && activeUploads.every((upload) => upload.status === "processing");
  const allUploadsSettled = panelUploads.length > 0 && activeUploads.length === 0;

  if (activeUploads.length > 0 && hasSeenRefetchStart) {
    setHasSeenRefetchStart(false);
  }
  if (allUploadsSettled && isRefetchingBankingSources && !hasSeenRefetchStart) {
    setHasSeenRefetchStart(true);
  }
  const isCalculatingCoverage = allUploadsSettled && (isRefetchingBankingSources || !hasSeenRefetchStart);
  const isPanelUploadInProgress = activeUploads.length > 0 || isCalculatingCoverage;
  const failedUploads = panelUploads.filter((upload) => upload.status === "failed");
  const progressStatusText = isCalculatingCoverage
    ? intl.formatMessage(MESSAGES.calculatingCoverageText)
    : isProcessing
    ? intl.formatMessage(MESSAGES.extractionProcessingText)
    : null;

  if (allUploadsSettled && !isCalculatingCoverage && failedUploads.length === 0 && pendingFiles.size > 0) {
    setPendingFiles(new Set());
    setHasSeenRefetchStart(false);
  }

  const handleClose = () => {
    if (isPanelUploadInProgress || failedUploads.length > 0) {
      onCloseWithActiveUploads();
    }
    setRejectedFileNames([]);
    onClose();
  };

  // Moving/removing a group's only remaining source empties it (and, per the backend, deletes
  // it), so close the panel instead of leaving it showing a group that no longer exists. Removing
  // a statement also empties the group if every statement member here came from the same
  // multi-account upload being removed - the backend detaches all of an upload's memberships at
  // once, not just the one the action was triggered from.
  const isOnlySource = group.connectionMembers.length + group.statementMembers.length === 1;
  const emptiesGroupOnRemoval = (uploadId: string) =>
    group.connectionMembers.length === 0 && group.statementMembers.every((member) => member.id === uploadId);
  const removalWouldEmptyGroup = statementToRemove !== null && emptiesGroupOnRemoval(statementToRemove.id);
  const handleSourceChanged = () => {
    onStatementsChanged();
    if (isOnlySource || removalWouldEmptyGroup) {
      onClose();
    }
  };

  const connectionMember = group.connectionMembers[0];
  const hasConnection = Boolean(connectionMember);
  const hasDisconnectedConnection = hasDisconnectedConnectionMember(group);
  // Fixed once per render so the bar and body text always agree on "now".
  const now = dayjs();
  const rawMonthCoverage = getMonthCoverage(group, requiredStatementMonths, now, intl.locale);
  const isReviewed = getAccountReviewState(group, requiredStatementMonths, now) === "reviewed";
  const monthCoverage = isReviewed ? markGapMonthsReviewed(rawMonthCoverage) : rawMonthCoverage;
  const canAcknowledgeCoverage = hasStatementCoverageGap(group, requiredStatementMonths, now);
  const coverageGapPeriods = getCoverageGapPeriods(intl, group, requiredStatementMonths, now);
  const { badgeStatus, badgeText } = getAccountReadinessBadge(intl, group, requiredStatementMonths, now);
  const coverageBody = hasDisconnectedConnection
    ? { kind: "text" as const, text: intl.formatMessage(MESSAGES.disconnectedBodyText) }
    : hasConnection
    ? getCompleteCoverageBodyText(intl)
    : getStatementCoverageBodyText(intl, group, requiredStatementMonths, now);
  const acceptedStatementFileTypes = isCsvEligibleGap(group, requiredStatementMonths, now)
    ? BANK_STATEMENT_ACCEPTED_FILE_TYPES
    : BANK_STATEMENT_ACCEPTED_FILE_TYPES_PDF_ONLY;
  const connection = connectionMember?.connection;

  const groupLogo = connection ? (
    <BankLogoDisplay logoState={logoLoadingStates[connection.institutionID]} />
  ) : (
    <IconBank24Line />
  );

  const statementRows = sortNewestFirst([
    ...group.statementMembers.map((member) => ({ kind: "own" as const, member })),
    ...reassignedStatementMembers.map((reassigned) => ({ kind: "reassigned" as const, ...reassigned })),
  ]);

  const unsubmittedUuids = group.statementMembers.filter((member) => !member.isSubmitted).map((member) => member.id);

  const handleCoverageAcknowledgementChange = async (acknowledged: boolean) => {
    try {
      await setCoverageAcknowledgement.mutateAsync({ groupUuids: [group.uuid], acknowledged });
      await invalidateBankingSourceQueries(queryClient, customerDomain);
    } catch (e: any) {
      openErrorModal({ variant: "submit-error", error: e });
      addError(e, { message: "group-details-panel: set coverage acknowledgement error" });
    }
  };

  const handleConfirm = async () => {
    if (unsubmittedUuids.length === 0) {
      onClose();
      return;
    }
    try {
      await submitByUuid.mutateAsync({ uuids: unsubmittedUuids, group: DEFAULT_GROUP_NAME });
      posthog?.capture("onboarding_bank_statement_submitted", {
        file_count: unsubmittedUuids.length,
        trigger: "group_confirm",
      });
      await invalidateBankingSourceQueries(queryClient, customerDomain);
      onClose();
    } catch (e: any) {
      openErrorModal({ variant: "submit-error", error: e });
      addError(e, { message: "group-details-panel: submit statements error" });
    }
  };

  return (
    <>
      <SidePanel
        open={isOpen}
        onClose={handleClose}
        title={getGroupAccountLabel(intl, group)}
        variant="md"
        dialogRef={dialogRef}
        primaryAction={{
          content: intl.formatMessage(MESSAGES.groupDetailsPanelConfirmButton),
          onAction: handleConfirm,
          loading: submitByUuid.isPending,
          disabled: submitByUuid.isPending,
        }}
      >
        {reconnectingConnectionId && (
          <ReconnectBankAccountModal
            bankConnectProps={bankConnectProps}
            customerSlug={customerDomain}
            basiqNextPath={AuthCallbackPath.FUNDING_APPLICATION_BANKING}
            connectionId={reconnectingConnectionId}
            onReconnected={() => {
              onStatementsChanged();
              setReconnectingConnectionId(null);
            }}
            onClose={() => setReconnectingConnectionId(null)}
          />
        )}
        <Flex direction="column" gap="10">
          <Flex direction="column" gap="2">
            <Flex justify="space-between" align="flex-start">
              <Flex gap="3" align="center">
                <Flex direction="column" gap="1">
                  <Text fontWeight="semiBold">
                    {connectionMember
                      ? capitalizeFirstLetter(connectionMember.account.name)
                      : getGroupAccountLabel(intl, group)}
                  </Text>
                  {group.accountType && (
                    <Text size="sm" color="subtle">
                      {group.currency
                        ? intl.formatMessage(MESSAGES.accountDetailsTypeAndCurrency, {
                            accountType: capitalize(group.accountType),
                            currency: group.currency,
                          })
                        : intl.formatMessage(MESSAGES.accountDetailsType, {
                            accountType: capitalize(group.accountType),
                          })}
                    </Text>
                  )}
                </Flex>
              </Flex>
              <Tag content={badgeText} color={badgeStatus} />
            </Flex>

            <Flex direction="column" gap="5">
              {coverageBody.kind === "fragmentedList" ? (
                <Flex direction="column" gap="2">
                  <Text size="sm">{coverageBody.intro}</Text>
                  <List>
                    {coverageBody.periods.map((period, index) => (
                      <List.Item key={index}>
                        <Text size="sm">{period}</Text>
                      </List.Item>
                    ))}
                  </List>
                  <Text size="sm">{coverageBody.outro}</Text>
                </Flex>
              ) : (
                <Text size="sm">{coverageBody.text}</Text>
              )}

              {!hasConnection && !isPanelUploadInProgress && <MonthsCoverageBar months={monthCoverage} />}

              {!hasConnection && isPanelUploadInProgress && (
                <Flex direction="column" gap="2">
                  <ProgressBar value={combinedProgress} maxValue={100} />
                  {progressStatusText ? (
                    <Text size="xs" color="subtle">
                      {progressStatusText}
                    </Text>
                  ) : (
                    <Flex justify="space-between">
                      <Text size="xs" color="subtle">
                        {intl.formatMessage(MESSAGES.uploadInProgressLabel)}
                      </Text>
                      <Text size="xs" color="subtle">
                        {intl.formatMessage(MESSAGES.uploadInProgressPercentage, { progress: combinedProgress })}
                      </Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>

          {failedUploads.length > 0 && (
            <Flex direction="column" gap="2">
              {failedUploads.map((upload, index) => (
                <Flex key={`${upload.file.name}-${index}`} justify="space-between" align="center" gap="2">
                  <Text size="xs" color="critical" truncate>
                    {upload.file.name}: {upload.error ?? intl.formatMessage(MESSAGES.extractionFailedText)}
                  </Text>
                  {upload.metadata !== EXTRACTION_FAILED_METADATA && (
                    <Button
                      variant="Tertiary"
                      size="small"
                      onClick={() => onFileRetry(upload.file, upload.uploadedFile)}
                    >
                      {intl.formatMessage(MESSAGES.retryAction)}
                    </Button>
                  )}
                </Flex>
              ))}
            </Flex>
          )}

          {rejectedFileNames.length > 0 && (
            <Flex direction="column" gap="2">
              {rejectedFileNames.map((name, index) => (
                <Text key={`${name}-${index}`} size="xs" color="critical" truncate>
                  {name}: {intl.formatMessage(MESSAGES.fileRejectedMessage)}
                </Text>
              ))}
            </Flex>
          )}

          <Flex direction="column" gap="10">
            <Flex direction="column" gap="4">
              <Flex gap="2">
                <Text size="sm" fontWeight="semiBold">
                  {intl.formatMessage(MESSAGES.sourcesSectionTitle)}
                </Text>
                <Text size="sm" color="subtle">
                  {group.connectionMembers.length +
                    group.statementMembers.length +
                    reassignedMembers.length +
                    reassignedStatementMembers.length}
                </Text>
              </Flex>
              <Flex direction="column" gap="2">
                {group.connectionMembers.map((member, index) => {
                  const isMemberDisconnected = !member.connection.accessible;
                  return (
                    <ConnectionItem
                      key={`${member.connection.id}-${index}`}
                      className={styles.sourceItem}
                      label={getGroupAccountLabel(intl, group)}
                      logo={groupLogo}
                      subtitle={getConnectionMemberSubtitle(intl, member)}
                      variant={isMemberDisconnected ? "critical" : undefined}
                      badgeStatus={isMemberDisconnected ? "critical" : undefined}
                      badgeText={isMemberDisconnected ? intl.formatMessage(MESSAGES.notConnectedBadge) : undefined}
                      actions={
                        isMemberDisconnected
                          ? [
                              {
                                content: intl.formatMessage(MESSAGES.reconnectAction),
                                onAction: () => {
                                  posthog?.capture("onboarding_bank_reconnection_started", {
                                    provider: member.connection.providerType,
                                  });
                                  // Basiq/TrueLayer redirect to a different origin and return via a fresh page load, which
                                  // wipes this state - stash an intent to reopen the panel. Plaid is an overlay on this same
                                  // page, so its OAuth bounce returns here directly and nothing unmounts; no intent needed.
                                  if (member.connection.providerType !== "plaid") {
                                    setReopenGroupIntent(customerDomain, group.uuid, []);
                                  }
                                  setReconnectingConnectionId(member.connection.id);
                                },
                              },
                            ]
                          : undefined
                      }
                    />
                  );
                })}
                {statementRows.map((row) =>
                  row.kind === "reassigned" ? (
                    <ConnectionItem
                      key={row.member.memberUuid}
                      className={styles.sourceItem}
                      label={row.member.fileName}
                      logo={<IconPage24Line />}
                      subtitle={intl.formatMessage(MESSAGES.reassignedStatementSubtitle, {
                        institutionLabel: getGroupAccountLabel(intl, row.destinationGroup),
                      })}
                      badgeStatus="info"
                      badgeText={intl.formatMessage(MESSAGES.reassignedBadge)}
                    />
                  ) : (
                    <StatementRow
                      key={row.member.memberUuid}
                      className={styles.sourceItem}
                      label={row.member.fileName}
                      logo={<IconPage24Line />}
                      onReassign={() => setStatementToReassign(row.member)}
                      onRemove={() => {
                        if (emptiesGroupOnRemoval(row.member.id)) {
                          setStatementToRemove(row.member);
                        } else {
                          removeStatement(row.member.id, { wasUnassigned: false, onSuccess: handleSourceChanged });
                        }
                      }}
                      isRemoveDisabled={isRemovePending}
                    />
                  )
                )}
                {reassignedMembers.map(({ member, destinationGroup }) => (
                  <ConnectionItem
                    key={member.memberUuid}
                    className={styles.sourceItem}
                    label={getAccountLabel(intl, member.connection, member.account)}
                    logo={<BankLogoDisplay logoState={logoLoadingStates[member.connection.institutionID]} />}
                    subtitle={intl.formatMessage(MESSAGES.reassignedSubtitle, {
                      institutionLabel: getGroupAccountLabel(intl, destinationGroup),
                    })}
                    badgeStatus="info"
                    badgeText={intl.formatMessage(MESSAGES.reassignedBadge)}
                  />
                ))}
              </Flex>
            </Flex>

            {!hasConnection && (
              <Flex direction={isMobile ? "column" : "row"} gap="2">
                <Button variant="Secondary" size="small" fullWidth={isMobile} onClick={onConnect}>
                  {intl.formatMessage(MESSAGES.addAccountInstantly)}
                </Button>
                <FileUploadTrigger
                  onFilesAdded={handleFilesAdded}
                  acceptedFileTypes={acceptedStatementFileTypes}
                  sizeLimitMB={UPLOAD_STATEMENTS_SIZE_LIMIT_MB}
                  fullScreenDropHighlightContainerRef={dialogRef}
                  testId="group-details-panel-statements-trigger"
                  trigger={({ open, disabled: triggerDisabled }) => (
                    <Button
                      variant="Tertiary"
                      size="small"
                      fullWidth={isMobile}
                      onClick={open}
                      disabled={triggerDisabled}
                    >
                      {intl.formatMessage(
                        isMobile ? MESSAGES.addStatementsMobile : MESSAGES.sourcesAddStatementsButton
                      )}
                    </Button>
                  )}
                />
              </Flex>
            )}

            {canAcknowledgeCoverage && (
              <Checkbox
                size="small"
                isSelected={group.isIncompleteCoverageAcknowledged}
                isDisabled={setCoverageAcknowledgement.isPending}
                onChange={handleCoverageAcknowledgementChange}
              >
                <Text size="sm">
                  {intl.formatMessage(MESSAGES.coverageAcknowledgementLabel, {
                    count: coverageGapPeriods.length,
                    period: intl.formatList(coverageGapPeriods),
                  })}
                </Text>
              </Checkbox>
            )}
          </Flex>
        </Flex>
      </SidePanel>

      <AssignStatementModal
        key={`assign-${statementToReassign?.id ?? "none"}`}
        isOpen={statementToReassign !== null}
        onClose={() => setStatementToReassign(null)}
        fileName={statementToReassign?.fileName ?? ""}
        uploadUuid={statementToReassign?.id ?? ""}
        memberUuid={statementToReassign?.memberUuid}
        currentGroupUuid={group.uuid}
        accountGroups={accountGroups}
        companyDomain={companyDomain}
        onMoved={handleSourceChanged}
      />

      <RemoveStatementModal
        key={`remove-${statementToRemove?.id ?? "none"}`}
        isOpen={statementToRemove !== null}
        onClose={() => setStatementToRemove(null)}
        uploadUuid={statementToRemove?.id ?? ""}
        group={group}
        companyDomain={companyDomain}
        onRemoved={handleSourceChanged}
      />
    </>
  );
};
